import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  Observable,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { CONFIGURATION } from '../../app.config';
import { UserService } from '../user/user.service';
import { User, UserRole } from '../user/user.types';
import { JwtPayload } from './auth.types';
import { AuthUtils } from './auth.utils';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _authenticated: boolean = false;
  private _httpClient = inject(HttpClient);
  private _userService = inject(UserService);

  private baseUrl = inject(CONFIGURATION).apiBaseUrl;

  readonly isRequestingToken$ = new BehaviorSubject(false);

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  private _storageJwtTokenKey = 'sb_backoffice_jwtToken';
  private _storageRefreshTokenKey = 'sb_backoffice_refreshToken';

  /**
   * Setter & getter for JWT token
   */
  set jwtToken(token: string) {
    localStorage.setItem(this._storageJwtTokenKey, token);
  }

  get jwtToken(): string {
    return localStorage.getItem(this._storageJwtTokenKey) ?? '';
  }

  set refreshToken(token: string) {
    localStorage.setItem(this._storageRefreshTokenKey, token);
  }

  get refreshToken(): string {
    return localStorage.getItem(this._storageRefreshTokenKey) ?? '';
  }

  private decodeToken(token: string): JwtPayload | null {
    const parsedToken = AuthUtils.parseJwtToken(token);
    if (!parsedToken) {
      return null;
    }
    return {
      sub: parsedToken[
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
      ],
      name: parsedToken[
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'
      ],
      email:
        parsedToken[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
        ],
      role: parsedToken[
        'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
      ] as UserRole,
    };
  }

  private convertTokenToUser(token: JwtPayload): User {
    return {
      id: token.sub,
      email: token.email,
      name: token.name,
      status: 'online',
      role: token.role,
    };
  }

  private updateUserFromToken(token: string) {
    const parsedToken = this.decodeToken(token);
    if (parsedToken) {
      this._userService.user = this.convertTokenToUser(parsedToken);
    }
  }

  initialize() {
    const jwtToken = this.jwtToken;
    const refreshToken = this.refreshToken;

    if (jwtToken) {
      if (AuthUtils.isTokenExpired(jwtToken) && !refreshToken) {
        return this.signOut();
      }

      if (AuthUtils.isTokenExpired(jwtToken) && refreshToken) {
        return this.signInUsingToken().pipe(
          switchMap((result) => (result ? of(null) : this.signOut())),
        );
      }

      this._authenticated = true;

      this.updateUserFromToken(jwtToken);
    }

    return of(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Forgot password
   *
   * @param email
   */
  forgotPassword(email: string): Observable<any> {
    return this._httpClient.post(
      `${this.baseUrl}/api/admin/BackofficeAuth/send-reset-password`,
      { email },
    );
  }

  /**
   * Reset password
   *
   * @param email
   * @param token
   * @param newPassword
   */
  resetPassword(request: {
    email: string;
    token: string;
    newPassword: string;
  }): Observable<any> {
    return this._httpClient
      .post(`${this.baseUrl}/api/admin/BackofficeAuth/reset-password`, request)
      .pipe(
        catchError((error) => {
          return throwError(() => {
            // Utente non trovato
            if (error.error === 'User not found') {
              return 'Non riusciamo a trovare un account associato a questa email.';
            }

            // Utente non trovato
            if (error.error === 'Invalid token.') {
              return 'Il link per reimpostare la password non è valido o è scaduto. Richiedine uno nuovo.';
            }

            // Errore di connessione o errore API sconosciuto
            return "Si è verificato un errore inaspettato. Riprova più tardi o contatta l'assistenza tecnica fornendo i dettagli dell'azione che stavi compiendo.";
          });
        }),
      );
  }

  /**
   * Reset password
   *
   * @param email
   * @param currentPassword
   * @param newPassword
   */
  changePassword(request: {
    email: string;
    currentPassword: string;
    newPassword: string;
  }): Observable<any> {
    return this._httpClient.post(
      `${this.baseUrl}/api/admin/BackofficeAuth/change-password`,
      request,
    );
  }

  /**
   * Sign in
   *
   * @param credentials
   */
  signIn(credentials: { email: string; password: string }): Observable<{
    jwtToken: string;
    refreshToken: string;
  }> {
    // Throw error, if the user is already logged in
    if (this._authenticated) {
      return throwError(() => 'User is already logged in.');
    }

    return this._httpClient
      .post(`${this.baseUrl}/api/admin/BackofficeAuth/login`, credentials)
      .pipe(
        catchError((error) => {
          return throwError(() => {
            // Utente non trovato
            if (error.error === 'User not found') {
              return 'Non riusciamo a trovare un account associato a questa email.';
            }

            // Email non confermata
            if (error.error === 'Email not confirmed yet') {
              return "L'account è stato creato ma l'email non è ancora stata confermata. Controlla la tua casella di posta (e lo spam) per completare la registrazione.";
            }

            // Email o password errati
            if (error.error === 'Wrong email or password') {
              return "Email o password errati. Assicurati che le credenziali siano corrette o usa la funzione 'Password dimenticata'.";
            }

            // Errore di connessione o errore API sconosciuto
            return "Si è verificato un errore inaspettato. Riprova più tardi o contatta l'assistenza tecnica fornendo i dettagli dell'azione che stavi compiendo.";
          });
        }),
        switchMap((response: any) => {
          // Store the access token in the local storage
          this.jwtToken = response.jwtToken;
          this.refreshToken = response.refreshToken;

          // Set the authenticated flag to true
          this._authenticated = true;

          // Store the user on the user service
          this.updateUserFromToken(response.jwtToken);

          // Return a new observable with the response
          return of(response);
        }),
      );
  }

  /**
   * Sign in using the access token
   */
  signInUsingToken(): Observable<any> {
    this.isRequestingToken$.next(true);

    // Sign in using the token
    return this._httpClient
      .post(`${this.baseUrl}/api/admin/BackofficeAuth/login-token`, {
        token: this.refreshToken,
      })
      .pipe(
        catchError(() =>
          // Return false
          of(false),
        ),
        switchMap((response: any) => {
          // Replace the access token with the new one if it's available on
          // the response object.
          //
          // This is an added optional step for better security. Once you sign
          // in using the token, you should generate a new one on the server
          // side and attach it to the response object. Then the following
          // piece of code can replace the token with the refreshed one.
          if (response.jwtToken) {
            this.jwtToken = response.jwtToken;
          }

          // Set the authenticated flag to true
          this._authenticated = true;

          // Store the user on the user service
          this.updateUserFromToken(response.jwtToken);

          // Return true
          return of(true);
        }),
        finalize(() => {
          this.isRequestingToken$.next(false);
        }),
      );
  }

  /**
   * Sign out
   */
  signOut(): Observable<any> {
    // Remove the access token from the local storage
    localStorage.removeItem(this._storageJwtTokenKey);
    localStorage.removeItem(this._storageRefreshTokenKey);

    // Set the authenticated flag to false
    this._authenticated = false;

    this._userService.user = null;

    // Return the observable
    return of(true);
  }

  /**
   * Sign up
   *
   * @param user
   */
  signUp(user: {
    email: string;
    password: string;
    company: string;
  }): Observable<void> {
    return this._httpClient
      .post<void>(`${this.baseUrl}/api/admin/BackofficeAuth/register`, user)
      .pipe(
        catchError((error) => {
          return throwError(() => {
            // Email già in uso
            if (error.error === 'User already exists') {
              return "Questa email risulta già registrata. Accedi al tuo account oppure reimposta la password se l'hai dimenticata.";
            }

            // Errore di connessione o errore API sconosciuto
            return "Si è verificato un errore inaspettato. Riprova più tardi o contatta l'assistenza tecnica fornendo i dettagli dell'azione che stavi compiendo.";
          });
        }),
      );
  }

  confirmEmail(request: { email: string; token: string }): Observable<any> {
    return this._httpClient
      .post(`${this.baseUrl}/api/admin/BackofficeAuth/confirm-email`, request)
      .pipe(
        catchError((error) => {
          return throwError(() => {
            // Utente non trovato
            if (error.error === 'User not found') {
              return 'Non riusciamo a trovare un account associato a questa email.';
            }

            // Errore di connessione o errore API sconosciuto
            return "Si è verificato un errore inaspettato. Riprova più tardi o contatta l'assistenza tecnica fornendo i dettagli dell'azione che stavi compiendo.";
          });
        }),
      );
  }

  /**
   * Unlock session
   *
   * @param credentials
   */
  unlockSession(credentials: {
    email: string;
    password: string;
  }): Observable<any> {
    return this._httpClient.post(
      `${this.baseUrl}/api/admin/BackofficeAuth/unlock-session`,
      credentials,
    );
  }

  /**
   * Check the authentication status
   */
  check(): Observable<boolean> {
    // Check if the user is logged in
    if (this._authenticated) {
      return of(true);
    }

    // Check the access token availability
    if (!this.jwtToken) {
      return of(false);
    }

    // Check the access token expire date
    if (AuthUtils.isTokenExpired(this.jwtToken)) {
      return of(false);
    }

    // If the access token exists, and it didn't expire, sign in using it
    return this.signInUsingToken();
  }
}
